import { useCallback } from 'react'
import { isEmpty } from 'lodash'
import { TCertificationSchema } from 'transfix-form/forms/CarrierCertifications/components/CertificationModal/schema/CertificationSchema'
import { IUseCarrierLoadPreferencesSubmitter } from 'transfix-form/forms/CarrierCertifications/components/CertificationModal/types'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { ICertificationCode, Maybe } from 'types/FMSGraphqlTypes'
import { useCarrierProfileQuery } from '../../../../Carrier/graphql/CarrierProfile'
import { TOAST_ERROR_MESSAGE } from '../../../constants'
import { useDeleteCertificationMutation } from '../graphql/DeleteCertificationMutation'
import { useUpdateCertificationMutation } from '../graphql/UpdateCertificationMutation'
import { getUpdateCarrierCertificationInput } from '../utils'

const TOAST_ADD_SUCCESS_MESSAGE = 'Certification was added successfully'
const TOAST_EDIT_SUCCESS_MESSAGE = 'Certification was updated successfully'
const TOAST_DELETE_SUCCESS_MESSAGE = 'Certification was deleted successfully'

export const useCarrierDiverseCertificationsCardSubmitter = (
  carrierApplicationId: string,
  currentCertificationCode: Maybe<ICertificationCode>
): IUseCarrierLoadPreferencesSubmitter<TCertificationSchema> => {
  const { openToast } = useGlobalToast()

  const showErrorToast = useCallback(() => {
    openToast({ toastMessage: TOAST_ERROR_MESSAGE, toastType: 'alert' })
  }, [openToast])

  const {
    loading: fetchCarrierProfileLoading,
    refetch: fetchCarrierProfile,
  } = useCarrierProfileQuery({
    // using regular query over lazy so that request can be awaited
    skip: true,
    variables: {
      id: carrierApplicationId,
    },
  })

  const [
    updateCertification,
    { loading: updateCertificationLoading },
  ] = useUpdateCertificationMutation()

  const [
    deleteCertification,
    { loading: deleteCertificationLoading },
  ] = useDeleteCertificationMutation()

  const submitHandler = useCallback(
    async (formValues: TCertificationSchema) => {
      const input = getUpdateCarrierCertificationInput({ formValues, carrierApplicationId })
      if (!input) {
        return false
      }

      try {
        const { data, errors } = await updateCertification({
          variables: {
            input,
          },
        })
        const executionErrors = data?.updateCarrierCertification?.errors

        if (isEmpty(errors) && isEmpty(executionErrors)) {
          // refetch if not updating
          if (!currentCertificationCode) {
            await fetchCarrierProfile({ id: carrierApplicationId })
          }

          openToast({
            toastMessage: currentCertificationCode
              ? TOAST_EDIT_SUCCESS_MESSAGE
              : TOAST_ADD_SUCCESS_MESSAGE,
          })
          return true
        }
        showErrorToast()
        return false
      } catch {
        showErrorToast()
        return false
      }
    },
    [
      carrierApplicationId,
      currentCertificationCode,
      fetchCarrierProfile,
      openToast,
      showErrorToast,
      updateCertification,
    ]
  )

  const deleteHandler = useCallback(async () => {
    if (!currentCertificationCode) {
      return false
    }

    try {
      const { data, errors } = await deleteCertification({
        variables: {
          input: {
            carrierApplicationId,
            code: currentCertificationCode,
          },
        },
      })
      const executionErrors = data?.deleteCarrierCertification?.errors

      if (isEmpty(errors) && isEmpty(executionErrors)) {
        await fetchCarrierProfile({ id: carrierApplicationId })
        openToast({ toastMessage: TOAST_DELETE_SUCCESS_MESSAGE })
        return true
      }
      showErrorToast()
      return false
    } catch {
      showErrorToast()
      return false
    }
  }, [
    carrierApplicationId,
    currentCertificationCode,
    deleteCertification,
    fetchCarrierProfile,
    openToast,
    showErrorToast,
  ])

  return {
    loading: deleteCertificationLoading || updateCertificationLoading || fetchCarrierProfileLoading,
    submitHandler,
    deleteHandler,
  }
}
